import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ProfessionalProfileSetup from './ProfessionalProfileSetup';
import AccountDashboard from './AccountDashboard';
import { Card, CardContent } from './ui/card';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProfileRouter = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [hasCompletedProfile, setHasCompletedProfile] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    checkProfileStatus();
  }, []);

  const checkProfileStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.error('❌ No auth token found');
        setError('Please log in to view your profile');
        setLoading(false);
        return;
      }
      
      // Check if user has a detailed profile
      const response = await axios.get(`${API}/detailed-profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data?.exists && response.data?.profile) {
        // User has completed profile data
        setHasCompletedProfile(true);
        console.log('✅ User has completed profile - showing dashboard');
      } else {
        // User needs to complete profile
        setHasCompletedProfile(false);
        console.log('ℹ️ User needs to complete profile - showing setup');
      }
    } catch (error) {
      if (error.response?.status === 404 || error.response?.status === 401) {
        // No profile found or unauthorized - needs setup
        setHasCompletedProfile(false);
        console.log('ℹ️ No profile found - showing setup');
      } else {
        console.error('Error checking profile status:', error);
        setError('Failed to load profile status');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-rose-900/50 to-pink-900/80"></div>
        
        <div className="relative z-10 min-h-screen flex items-center justify-center px-4">
          <Card className="w-full max-w-md bg-black/20 backdrop-blur-xl border border-white/10 shadow-2xl rounded-3xl">
            <CardContent className="text-center py-12">
              <div className="w-16 h-16 border-4 border-rose-400 border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
              <h2 className="text-2xl font-bold text-white mb-4">Loading Profile</h2>
              <p className="text-gray-300">Checking your profile status</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-rose-900/50 to-pink-900/80"></div>
        
        <div className="relative z-10 min-h-screen flex items-center justify-center px-4">
          <Card className="w-full max-w-md bg-black/20 backdrop-blur-xl border border-white/10 shadow-2xl rounded-3xl">
            <CardContent className="text-center py-12">
              <h2 className="text-2xl font-bold text-white mb-4">Profile Error</h2>
              <p className="text-gray-300 mb-6">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="bg-rose-600 hover:bg-rose-700 text-white px-6 py-3 rounded-xl"
              >
                Try Again
              </button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Route to appropriate component based on profile status
  // Allow override via URL parameter to skip to dashboard
  const urlParams = new URLSearchParams(window.location.search);
  const skipSetup = urlParams.get('skip') === 'true';
  
  if (hasCompletedProfile || skipSetup) {
    return <AccountDashboard />;
  } else {
    return <ProfessionalProfileSetup />;
  }
};

export default ProfileRouter;